/**
 * Pixro Analytics — server-side Meta CAPI relay (marketing site).
 *
 * Events POST to the core backend (`${API_URL}/analytics/event`, see config.ts),
 * which forwards them to the Meta Conversions API. This bypasses ad-blockers and
 * improves signal quality because the request originates from the server.
 *
 * The browser Meta Pixel (see metaPixel.ts) sets the `_fbp` / `_fbc` cookies that
 * we attach here for browser↔server matching. This site is logged-out, but forms
 * collect an email/phone we can pass for match quality.
 *
 * Usage:
 *   trackPageView();
 *   trackViewContent('AI Background Removal');
 *   trackInitiateCheckout('Dukaan Ads', { email, phone });
 *   trackLead('Contact Form', { email });
 */

import { API_URL } from "./config";
import { trackGaEvent } from "./googleAnalytics";

const BASE_URL = API_URL ?? "";

interface AnalyticsUserData {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    externalId?: string;
    fbp?: string;
    fbc?: string;
}

interface TrackEventOptions {
    userData?: AnalyticsUserData;
    customData?: Record<string, unknown>;
    sourceUrl?: string;
    eventId?: string;
}

/** Read the _fbp cookie (set by the Meta Pixel on first page load). */
function getFbpCookie(): string | undefined {
    if (typeof document === "undefined") return undefined;
    const match = document.cookie.match(/(?:^|;)\s*_fbp=([^;]+)/);
    return match ? match[1] : undefined;
}

/** Read the _fbc cookie (set when the user arrives via a Facebook ad click). */
function getFbcCookie(): string | undefined {
    if (typeof document === "undefined") return undefined;
    const match = document.cookie.match(/(?:^|;)\s*_fbc=([^;]+)/);
    return match ? match[1] : undefined;
}

/**
 * Fire a Meta CAPI event via the backend relay.
 * Silently fails if the request errors — analytics must never break the UI.
 */
export async function trackEvent(
    eventName: string,
    options: TrackEventOptions = {},
): Promise<void> {
    try {
        if (typeof window === "undefined") return; // SSR/prerender no-op
        const fbp = getFbpCookie();
        const fbc = getFbcCookie();

        const payload = {
            eventName,
            eventId: options.eventId,
            sourceUrl: options.sourceUrl ?? window.location.href,
            customData: options.customData,
            userData: {
                ...options.userData,
                ...(fbp ? { fbp } : {}),
                ...(fbc ? { fbc } : {}),
            },
        };

        await fetch(`${BASE_URL}/analytics/event`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            keepalive: true, // best-effort: don't block page unload
        });
    } catch {
        // Intentionally swallowed — analytics must never break business logic
    }
}

/** Convenience: fire PageView (call on every route change). */
export function trackPageView(userData?: AnalyticsUserData): void {
    void trackEvent("PageView", { userData });
}

/** Convenience: fire ViewContent (call when a user lands on a tool/solution page). */
export function trackViewContent(
    contentName: string,
    userData?: AnalyticsUserData,
): void {
    void trackEvent("ViewContent", {
        userData,
        customData: { content_name: contentName },
    });
}

/** Convenience: fire InitiateCheckout (call when a user starts a payment). */
export function trackInitiateCheckout(
    contentName: string,
    userData?: AnalyticsUserData,
): void {
    void trackEvent("InitiateCheckout", {
        userData,
        customData: { content_name: contentName },
    });
}

/** Convenience: fire Lead (call on a successful form submission). */
export function trackLead(
    contentName: string,
    userData?: AnalyticsUserData,
): void {
    void trackEvent("Lead", {
        userData,
        customData: { content_name: contentName },
    });
}

/* ──────────────────────────────────────────────────────────────────────────
 * DukaanAds funnel events.
 *
 * Each helper fires to BOTH sinks:
 *   - GA4   via gtag (browser) using GA4 recommended ecommerce event names.
 *   - Meta  via the CAPI relay (server-side) using Meta standard event names,
 *           attributed to the same Pixel ID.
 * GA params never carry PII; phone (for Meta match quality) only goes to the
 * CAPI relay, which hashes it server-side. All calls are best-effort/no-throw.
 * ────────────────────────────────────────────────────────────────────────── */

const INR = "INR";
const itemOf = (plan: string, valueRupees: number) => ({
    item_id: plan,
    item_name: `DukaanAds ${plan}`,
    item_category: "dukaan_ads",
    price: valueRupees,
    quantity: 1,
});

/** Landing page viewed (fire once on mount). */
export function trackDukaanView(locale: string): void {
    trackGaEvent("view_item", {
        item_category: "dukaan_ads",
        content_name: "DukaanAds",
        locale,
    });
    void trackEvent("ViewContent", {
        customData: { content_name: "DukaanAds", locale },
    });
}

/** A plan was chosen and the lead/payment sheet opened. */
export function trackDukaanSelectPlan(plan: string, valueRupees: number): void {
    trackGaEvent("add_to_cart", {
        currency: INR,
        value: valueRupees,
        items: [itemOf(plan, valueRupees)],
    });
    void trackEvent("AddToCart", {
        customData: { content_name: plan, value: valueRupees, currency: INR },
    });
}

/** Lead captured (shop/city/phone submitted, order created). */
export function trackDukaanLead(
    plan: string,
    valueRupees: number,
    userData?: AnalyticsUserData,
): void {
    trackGaEvent("generate_lead", { currency: INR, value: valueRupees, plan });
    void trackEvent("Lead", {
        userData,
        customData: { content_name: plan, value: valueRupees, currency: INR },
    });
}

/** Razorpay checkout opened. */
export function trackDukaanBeginCheckout(
    plan: string,
    valueRupees: number,
    userData?: AnalyticsUserData,
): void {
    trackGaEvent("begin_checkout", {
        currency: INR,
        value: valueRupees,
        items: [itemOf(plan, valueRupees)],
    });
    void trackEvent("InitiateCheckout", {
        userData,
        customData: { content_name: plan, value: valueRupees, currency: INR },
    });
}

/** Payment verified — the conversion. `orderId` dedupes Meta browser/CAPI. */
export function trackDukaanPurchase(
    orderId: string,
    plan: string,
    valueRupees: number,
    userData?: AnalyticsUserData,
): void {
    trackGaEvent("purchase", {
        transaction_id: orderId,
        currency: INR,
        value: valueRupees,
        items: [itemOf(plan, valueRupees)],
    });
    void trackEvent("Purchase", {
        eventId: orderId,
        userData,
        customData: {
            content_name: plan,
            value: valueRupees,
            currency: INR,
            order_id: orderId,
        },
    });
}

/** WhatsApp CTA clicked (low-friction contact). `source` = which CTA. */
export function trackDukaanWhatsApp(source: string): void {
    trackGaEvent("whatsapp_click", { source, content_name: "DukaanAds" });
    void trackEvent("Contact", {
        customData: { content_name: "DukaanAds", source },
    });
}

/**
 * DukaanAds micro-engagement event (GA4 only).
 *
 * For in-page interactions that aren't Meta standard conversions — CTA clicks,
 * gallery taps, FAQ opens, lead-form validation, payment errors. These help map
 * where visitors drop off before the AddToCart → Lead → Purchase funnel. Meta
 * CAPI is reserved for the standard conversion events above. Best-effort/no-throw.
 */
export function trackDukaanEngagement(
    eventName: string,
    params: Record<string, unknown> = {},
): void {
    trackGaEvent(eventName, { content_name: "DukaanAds", ...params });
}
